/*
  Clone of ExpandedHistoryRows for the delivery-and-payment view (EMS-10148).
  Identical to the original except that the Uploaded column is merged into the
  File column, with the upload timestamp rendered beneath the file name — so
  this grid has one fewer column and must stay in step with
  InvoiceDeliveryTableWithStripePayment's column list.
*/
import React from 'react'
import styles from './ExpandedHistoryRows.module.scss'
import { Popconfirm, Spin } from 'antd'
import {
  DeleteOutlined,
  FileExcelOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useGetDeliveryHistoryQuery } from '../../api/invoice-delivery-api-client'
import {
  ActionButtonsRow,
  ArchiveButton,
  CompanyCell,
  FileNameCell,
  IconButton,
  StatusBadge,
  UnmatchedTag,
  VersionBadge,
} from './BulkInvoiceUpload.styles'
import { UploadedMeta } from './UploadedMeta'
import { TruncatedFileName } from './TruncatedFileName'
import { EmailTemplateTag } from './emailTemplates/EmailTemplateTag'
import {
  formatDueDate,
  resolveStatusBadgeKey,
  resolveStatusLabel,
} from './utils'

interface ExpandedHistoryRowsWithStripePaymentProps {
  deliveryId: string
  onDelete: (id: string) => void
  onSetArchived: (id: string, isArchived: boolean) => void
  isMutating: boolean
  /** False shows file names as plain text instead of download links. */
  canDownload: boolean
  onDownload: (e: React.MouseEvent, url: string, fileName: string) => void
  /**
   * Whether the list is currently including archived deliveries. History
   * follows the same scope, so hiding archived rows in the table hides them
   * here too rather than surfacing them one row below.
   */
  showArchived: boolean
  /**
   * Column widths measured from the parent AntD Table's <colgroup>, in column
   * order: [selection, expand, Company, File, Email Status, Payment Method,
   * Amount, Due date, Actual Amount, Drawn Status, Actions]. Must stay in step with
   * InvoiceDeliveryTableWithStripePayment's column list — one cell per
   * column, or every history row shifts out of alignment.
   * Sharing the real rendered widths ensures grid cells align exactly with
   * parent row cells regardless of AntD sizing internals.
   */
  columnWidths: readonly number[]
}

function buildGridTemplate(widths: readonly number[]): string {
  return widths.map((w) => `${w}px`).join(' ')
}

function sumWidths(widths: readonly number[]): number {
  return widths.reduce((acc, w) => acc + w, 0)
}

const ExpandedHistoryRowsWithStripePayment: React.FC<
  ExpandedHistoryRowsWithStripePaymentProps
> = ({
  deliveryId,
  onDelete,
  onSetArchived,
  isMutating,
  canDownload,
  onDownload,
  columnWidths,
  showArchived,
}) => {
  const { data, isLoading, isError, error } = useGetDeliveryHistoryQuery(
    deliveryId,
    showArchived ? undefined : false
  )

  const gridTemplate = buildGridTemplate(columnWidths)
  const gridMinWidth = sumWidths(columnWidths)

  const gridVars = {
    '--grid-template': gridTemplate,
    '--grid-min-width': `${gridMinWidth}px`,
  } as React.CSSProperties

  if (isLoading) {
    return (
      <div className={styles.scrollWrapper}>
        <div className={styles.rowGrid} style={gridVars}>
          <div />
          <div />
          <span
            className={`${styles.cellSpanMessage} ${styles.cellMessageMuted}`}
          >
            <Spin size="small" /> Loading version history...
          </span>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={styles.scrollWrapper}>
        <div className={styles.rowGrid} style={gridVars}>
          <div />
          <div />
          <span
            className={`${styles.cellSpanMessage} ${styles.cellMessageError}`}
          >
            Failed to load history: {error?.message ?? 'Unknown error'}
          </span>
        </div>
      </div>
    )
  }

  const versions = (data?.versions ?? []).filter((version) => !version.isLatest)
  if (versions.length === 0) {
    return null
  }

  return (
    <div className={styles.scrollWrapper}>
      <div className={styles.innerMinWidth} style={gridVars}>
        {versions.map((record) => (
          <div
            key={record.id}
            className={`${styles.rowGrid} ${record.isArchived ? styles.rowGridArchived : ''}`}
            style={gridVars}
            // One cell per parent column, or the grid shifts sideways. Counted
            // against the shared column list in deliveryTableColumns.test.
            data-testid="history-row"
          >
            <div />
            <div />
            <div className={styles.cell}>
              <CompanyCell>
                <div className={styles.cellInner}>
                  <span>
                    {record.clientName ?? (
                      <em className={styles.emptyDash}>-</em>
                    )}
                  </span>
                  <VersionBadge $isHistorical>v{record.version}</VersionBadge>
                </div>
                {!record.clientId && (
                  <UnmatchedTag>
                    <WarningOutlined /> Unmatched
                  </UnmatchedTag>
                )}
              </CompanyCell>
            </div>
            <div className={styles.cell}>
              <EmailTemplateTag template={record.invoiceTemplate} />
            </div>
            <div className={styles.cell}>
              <div className={styles.fileCellStack}>
                {record.downloadUrl && canDownload ? (
                  <FileNameCell
                    $clickable
                    onClick={(e) =>
                      onDownload(e, record.downloadUrl ?? '', record.fileName)
                    }
                  >
                    <FileExcelOutlined className={styles.fileIcon} />
                    <TruncatedFileName
                      name={record.fileName}
                      companyName={record.clientName}
                    />
                  </FileNameCell>
                ) : (
                  <FileNameCell>
                    <FileExcelOutlined className={styles.fileIcon} />
                    <TruncatedFileName
                      name={record.fileName}
                      companyName={record.clientName}
                    />
                  </FileNameCell>
                )}
                <UploadedMeta createdAt={record.createdAt} />
              </div>
            </div>
            <div className={styles.cell}>
              <StatusBadge $status={resolveStatusBadgeKey(record)}>
                {resolveStatusLabel(record)}
              </StatusBadge>
            </div>
            {/*
              Payment Method / Amount, then Due date, then Actual Amount /
              Drawn Status. A superseded version is never drawn against — only
              the latest version carries payment state — so the payment cells
              are spacers that keep this grid aligned with the parent's
              columns. The due date is a property of the version itself, so it
              is shown.
            */}
            <div className={styles.cell} />
            <div className={styles.cell} />
            <div className={styles.cell}>
              {formatDueDate(record.dueDate) ?? (
                <em className={styles.emptyDash}>-</em>
              )}
            </div>
            <div className={styles.cell} />
            <div className={styles.cell} />
            <div className={styles.cell}>
              {/* Every row here is a superseded version, which can never be
                  sent — so no send control is offered at all. */}
              <ActionButtonsRow>
                <ArchiveButton
                  $archived={record.isArchived}
                  onClick={(e) => {
                    e.stopPropagation()
                    onSetArchived(record.id, !record.isArchived)
                  }}
                  disabled={isMutating}
                >
                  {record.isArchived ? 'Unarchive' : 'Archive'}
                </ArchiveButton>
                <Popconfirm
                  title="Delete this version"
                  description="This will permanently remove this version. Are you sure?"
                  onConfirm={() => onDelete(record.id)}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                >
                  <IconButton $color="#dc2626" disabled={isMutating}>
                    <DeleteOutlined />
                  </IconButton>
                </Popconfirm>
              </ActionButtonsRow>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export { ExpandedHistoryRowsWithStripePayment }
